import emrService from '@/apis/emr/service';
import BaseTable from '@/components/bases/BaseTable';
import constants from '@/constants';
import emrTypeHelper from '@/helper/emrType';
import EmrCheckbox from '@/screens/CreateEmr/EmrInput/EmrCheckbox';
import EmrRangePicker from '@/screens/CreateEmr/EmrInput/EmrRangePicker';
import EmrSearch from '@/screens/CreateEmr/EmrInput/EmrSearch';
import EmrSelect from '@/screens/CreateEmr/EmrInput/EmrSelect';
import DetailContent from '@/screens/Timeline/components/DetailContent';
import useGlobalConfigs from '@/stores/globalConfigs';
import { usePhrStoreInContext } from '@/stores/phr';
import quickModal from '@/utils/quickModal';
import { Button, Col, Drawer, Flex, Row, Tag } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as React from 'react';

export interface IPersonalHistoryProps {}

interface IFilter {
  icd?: string;
  group?: string;
  start?: dayjs.Dayjs;
  end?: dayjs.Dayjs;
}

const PersonalHistory: React.FC<IPersonalHistoryProps> = ({ ...props }) => {
  const [openFilter, setOpenFilter] = React.useState<boolean>(false);
  const uid = usePhrStoreInContext((s) => s.uid);
  const emrTypes = useGlobalConfigs((s) => s.emrTypes);
  const [filter, setFilter] = React.useState<IFilter>();

  const objFilter = _.pickBy(
    {
      'data.icd.data.group': filter?.group,
      'data.icd.data.icd.value': filter?.icd,
    },
    _.identity,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const { data, fetchNextPage, isFetchingNextPage, refetch } = emrService.useGetEmrInfinite({
    uid,
    type: 'healthRecord',
    limit: 10,
    filter: objFilter ? JSON.stringify(objFilter) : undefined,
    start: filter?.start?.startOf('date').toDate(),
    end: filter?.end?.endOf('date').toDate(),
  });

  const healthRecordEmrType = emrTypes?.['healthRecord'];
  const icdEmrTypeConfig = emrTypes?.['icd'];
  const fieldGroupConfig = icdEmrTypeConfig?.format?.fields?.find((f) => f.key === 'group');
  const objRawDataSource: Record<string, IEMRData> = {};
  const dataSource = data?.pages?.reduce((d: IEMRData[], e: any) => {
    e?.data?.forEach((emr: IEMRData) => {
      objRawDataSource[emr._id] = emr;
      if (emr?.data?.icd?.length) {
        emr?.data?.icd?.forEach((icd: any) => {
          if (
            // Lọc theo icd
            (!filter?.icd || icd?.data?.icd?.value === filter?.icd) &&
            // Lọc theo nhóm bệnh
            (!filter?.group || icd?.data?.group === filter?.group)
          ) {
            const emrClone = _.cloneDeep(emr);
            emrClone.data.icd = [icd];
            d.push(emrClone);
          }
        });
      } else {
        d.push(emr);
      }
    });
    return d;
  }, []);

  return (
    <>
      <Drawer
        height={'auto'}
        placement="top"
        open={openFilter}
        title={
          <div className="flex justify-between items-center">
            <div>Bộ lọc tiền sử cá nhân</div>
            <Flex gap={8}>
              <Button
                danger
                onClick={() => {
                  setFilter({});
                  setOpenFilter(false);
                }}
              >
                Hủy lọc
              </Button>
              <Button onClick={() => setOpenFilter(false)}>Đóng</Button>
            </Flex>
          </div>
        }
        onClose={() => {
          setOpenFilter(false);
        }}
      >
        <Row gutter={8}>
          <Col span={12}>
            <EmrSelect
              value={filter?.group}
              onChange={(v) => setFilter((s) => ({ ...s, group: v }))}
              allowClear
              labelProps={{ name: 'group', required: false }}
              field={icdEmrTypeConfig?.format.fields?.find((f) => f.key === 'group')}
            />
          </Col>
          <Col span={12}>
            <EmrSearch
              allowClear
              labelProps={{ label: 'Tìm theo ICD', name: 'icd', required: false }}
              key={'icd'}
              field={icdEmrTypeConfig?.format.fields?.find((f) => f.key === 'icd')}
              value={filter?.icd}
              onChange={(v) => setFilter((s) => ({ ...s, icd: v }))}
            />
          </Col>
          <Col span={12}>
            <EmrRangePicker
              labelProps={{ label: 'Chọn khoảng thời gian', name: 'dateRange', required: false }}
              rangePickerProps={{
                value: [filter?.start, filter?.end],
                onChange: (values) =>
                  setFilter((s) => ({ ...s, start: values?.[0] as dayjs.Dayjs, end: values?.[1] as dayjs.Dayjs })),
                allowClear: true,
                format: constants.DATE_FORMAT,
              }}
            />
          </Col>
        </Row>
      </Drawer>
      <div className="px-2 pb-2 flex justify-end">
        <Button
          className=""
          onClick={() => {
            setOpenFilter(true);
          }}
        >
          Bộ lọc
        </Button>
      </div>
      <div
        className="max-h-[600px] overflow-y-auto px-2"
        onScroll={(e) => {
          const element: any = e.target;
          const { scrollTop, clientHeight, scrollHeight } = element;
          if (scrollTop + clientHeight >= scrollHeight - 50) {
            !isFetchingNextPage && fetchNextPage();
          }
        }}
      >
        <BaseTable
          loading={isFetchingNextPage}
          pagination={false}
          sticky
          dataSource={dataSource}
          columns={[
            {
              key: 'group',
              title: 'Nhóm bệnh',
              render(_, record: IEMRData) {
                return record?.data?.icd?.map((d: IEMRData, i: number) => {
                  const detailValue = emrTypeHelper.getOption(fieldGroupConfig as IFieldConfig, d?.data.group);
                  return detailValue ? (
                    <div key={i.toString()} className="p-1">
                      <Tag className="rounded-3xl w-full flex justify-center" color={detailValue?.color}>
                        {detailValue?.label}
                      </Tag>
                    </div>
                  ) : (
                    ''
                  );
                });
              },
            },
            {
              key: 'family',
              title: 'Tiền sử gia đình',
              render(value, record: IEMRData) {
                return (
                  <EmrCheckbox disabled checked={record?.data?.icd?.some((d: IEMRData) => d?.data.family === true)} />
                );
              },
            },
            {
              key: 'date',
              title: 'Thời gian',
              dataIndex: 'date',
              render(value) {
                return <>{value ? dayjs(value).format(constants.DATE_FORMAT) : ''}</>;
              },
            },
            {
              key: 'detail',
              title: 'Chẩn đoán',
              render(_, record: IEMRData) {
                return record?.data?.icd
                  ?.map((d: IEMRData) => {
                    return d?.data.definiteDiagnosis;
                  })
                  ?.join(', ');
              },
            },
            {
              key: 'icd',
              title: 'Mã ICD',
              render(_, record: IEMRData) {
                return record?.data?.icd
                  ?.map((d: IEMRData) => {
                    return d?.data?.icd?.label;
                  })
                  ?.join(', ');
              },
            },
            {
              key: 'healthRecord',
              title: 'Phiếu khám',
              render(_, record: IEMRData) {
                return (
                  <Button
                    onClick={() => {
                      quickModal.custom({
                        mask: true,
                        title: 'Hồ sơ bệnh án',
                        width: 900,
                        afterClose() {
                          refetch();
                        },
                        content: (
                          <DetailContent
                            emr={objRawDataSource?.[record._id]}
                            emrType={healthRecordEmrType as IEMRType}
                          />
                        ),
                      });
                    }}
                    className=""
                  >
                    {record?.data?.healthRecord || 'Phiếu khám'}
                  </Button>
                );
              },
            },
          ]}
        />
      </div>
    </>
  );
};

export default PersonalHistory;
